import React from "react";
import { useTranslation } from "react-i18next";
import { useCheckoutStore } from "../store/checkout-store";
import { useCart } from "@/features/cart";
import { CreditCard } from "lucide-react";

export const InstallmentOptions: React.FC = () => {
  const { t } = useTranslation();
  const { formatPrice } = useCart();
  const {
    installments,
    selectedInstallmentNumber,
    setSelectedInstallment,
    cardInfo,
  } = useCheckoutStore();

  if (!cardInfo || installments.length === 0) {
    return null;
  }

  // Seçili taksitin bilgilerini bul
  const selectedInstallment = installments.find(
    (inst) => inst.installmentNumber === selectedInstallmentNumber
  );

  return (
    <div className="border border-[#e3e8ef] dark:border-gray-700 rounded-[12px] overflow-hidden">
      {/* Header */}
      <div className="bg-[#fcfcfd] dark:bg-gray-800 border-b border-[#e3e8ef] dark:border-gray-700 px-4 py-3">
        <div className="flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-[#6172f3]" />
          <h3 className="text-[16px] font-semibold text-[#121926] dark:text-gray-100">
            {t("checkout.installmentOptions")}
          </h3>
        </div>
        {cardInfo.bankName && (
          <p className="text-[12px] text-[#697586] dark:text-gray-400 mt-1">
            {cardInfo.bankName} • {cardInfo.cardFamily}
          </p>
        )}
      </div>

      {/* Installment Options */}
      <div className="p-4 space-y-2">
        {installments.map((installment) => {
          const isSelected =
            selectedInstallmentNumber === installment.installmentNumber;
          const isSinglePayment = installment.installmentNumber === 1;

          return (
            <button
              key={installment.installmentNumber}
              onClick={() =>
                setSelectedInstallment(installment.installmentNumber)
              }
              className={`
                w-full flex items-center justify-between p-3 rounded-[10px] border transition-all
                ${
                  isSelected
                    ? "bg-[#f5f8ff] dark:bg-blue-950/30 border-[#6172f3]"
                    : "bg-white dark:bg-gray-800 border-[#e3e8ef] dark:border-gray-700 hover:border-[#6172f3]/50"
                }
              `}
            >
              <div className="flex items-center gap-3">
                {/* Radio Button */}
                <div
                  className={`
                    w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all
                    ${
                      isSelected
                        ? "border-[#6172f3] bg-white"
                        : "border-[#e3e8ef] dark:border-gray-600 bg-white dark:bg-gray-700"
                    }
                  `}
                >
                  {isSelected && (
                    <div className="w-2 h-2 rounded-full bg-[#6172f3]" />
                  )}
                </div>

                {/* Installment Info */}
                <div className="flex flex-col gap-0.5">
                  <p className="text-[14px] font-semibold text-[#121926] dark:text-gray-100 text-left">
                    {isSinglePayment
                      ? t("checkout.singlePayment")
                      : t("checkout.installment", {
                          count: installment.installmentNumber,
                        })}
                  </p>
                  {!isSinglePayment && (
                    <p className="text-[12px] text-[#697586] dark:text-gray-400 text-left">
                      {t("checkout.monthlyPayment")}:{" "}
                      {formatPrice(installment.installmentPrice)}
                    </p>
                  )}
                </div>
              </div>

              {/* Total Price */}
              <div className="flex flex-col items-end">
                <p className="text-[14px] font-semibold text-[#121926] dark:text-gray-100">
                  {formatPrice(installment.totalPrice)}
                </p>
                {!isSinglePayment && (
                  <p className="text-[10px] text-[#697586] dark:text-gray-400">
                    {t("checkout.total")}
                  </p>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Selected Installment Summary */}
      {selectedInstallment && (
        <div className="bg-[#f5f8ff] dark:bg-blue-950/30 border-t border-[#e3e8ef] dark:border-gray-700 px-4 py-3">
          <div className="flex items-center justify-between">
            <span className="text-[14px] font-medium text-[#121926] dark:text-gray-100">
              {selectedInstallment.installmentNumber === 1
                ? t("checkout.selectedSinglePayment")
                : t("checkout.selectedInstallment", {
                    count: selectedInstallment.installmentNumber,
                  })}
            </span>
            <span className="text-[16px] font-semibold text-[#6172f3]">
              {formatPrice(selectedInstallment.totalPrice)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};
